import React, { useState } from "react";
import { Card, CardHeader, Avatar, Typography, CardMedia, IconButton, Menu, MenuItem } from "@material-ui/core";
import { MoreVert as MoreVertIcon } from "@material-ui/icons";
import Markdown from "react-markdown";
import moment from "moment";
import { FeedItem } from "api/feed";
import { deleteStories } from "api/admin/stories";
import { useSnackbar } from "notistack";
import i18n from "./i18n";

// prop types
interface StoryFeedCardProps {
    allowMoreOptions?: boolean,
    feedItem: FeedItem,
}

// format date
const formatDate = (date: string) => {
    const m = moment(date);
    if (m.isSame(moment(), "day")) return m.format(`[${i18n.today}] [${i18n.at}] HH:mm`);
    if (m.isSame(moment().subtract(1, "day"), "day")) return m.format(`[${i18n.yesterday}] [${i18n.at}] HH:mm`);
    return m.format(`DD.MM.YYYY [${i18n.at}] HH:mm`);
};

// story feed item card
const StoryFeedCard = ({ allowMoreOptions, feedItem }: StoryFeedCardProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        error?.type === "insufficientPermissions" ? i18n.insufficientPermissions : `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [isLoading, setLoading] = useState(false);
    const [showMoreOptions, setShowMoreOptions] = useState<HTMLButtonElement | null>(null);

    // delete action
    const deleteStory = async () => {
        setShowMoreOptions(null);
        setLoading(true);
        try {
            await deleteStories({ storyId: feedItem.story!.id });
            enqueueSnackbar(i18n.deletedSuccessfully, { variant: "success" });
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // render
    return (
        <>
            {/* card */}
            <Card>
                <CardHeader
                    action={allowMoreOptions && (
                        <IconButton onClick={(e) => setShowMoreOptions(e.currentTarget)}>
                            <MoreVertIcon />
                        </IconButton>
                    )}
                    avatar={<Avatar src={feedItem.story!.author.profilePictureUrl} style={{ background: "#3D4249" }} />}
                    title={(
                        <>
                            {feedItem.story!.attachedPictureUrl && (
                                <CardMedia
                                    image={feedItem.story!.attachedPictureUrl}
                                    style={{ backgroundSize: "contain", height: 240 }}
                                />
                            )}
                            <Markdown linkTarget="_blank">{feedItem.story!.content}</Markdown>
                        </>
                    )}
                    subheader={(
                        <Typography color="textSecondary" variant="body2">
                            {formatDate(feedItem.story!.authoredAt)}
                            {" \u2022 "}
                            {`${feedItem.story!.author.firstName} ${feedItem.story!.author.lastName}`}
                        </Typography>
                    )}
                />
            </Card>

            {/* more options */}
            <Menu
                anchorEl={showMoreOptions}
                anchorOrigin={{ vertical: "center", horizontal: "center" }}
                getContentAnchorEl={null}
                open={!!showMoreOptions}
                onClose={() => setShowMoreOptions(null)}
            >
                <MenuItem disabled={isLoading} onClick={deleteStory}>{i18n.delete}</MenuItem>
            </Menu>
        </>
    );
};

export default StoryFeedCard;
